import {
  type SortDto,
  TABLE_COLUMN_SCHEDULE_FIELDS,
  type TableColumnSystemField,
} from '@tempo-io/roadmap-dto';
import type { CustomField, CustomFieldTypes } from '@/common/api/types/customField';

// The backend cannot resolve a single sort key across a MULTI_CUSTOMER field's several rows.
const UNSORTABLE_CUSTOM_FIELD_TYPES: ReadonlySet<CustomFieldTypes> = new Set(['MULTI_CUSTOMER']);

export const isCustomFieldTypeSortable = (type: CustomFieldTypes): boolean =>
  !UNSORTABLE_CUSTOM_FIELD_TYPES.has(type);

export const buildCustomFieldTypeById = (
  customFields: readonly CustomField[],
): ReadonlyMap<number, CustomFieldTypes> =>
  new Map(customFields.map((field) => [field.id, field.type]));

const COLUMN_TYPE_TO_ORDER_KEY: Partial<Record<TableColumnSystemField, string>> = {
  name: 'name',
  id: 'id',
  externalKey: 'externalKey',
  status: 'status',
  itemType: 'itemType',
  // Reporting-store only; `sortableKeys` gates it per branch.
  team: 'teamName',
  estimation: 'estimation',
  estimationScheme: 'estimationScheme',
  timeSpent: 'timeSpentSeconds',
  doneDate: 'doneDateTime',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  earliestStartDate: 'earliestStartDateTime',
  deadlineDate: 'deadlineDateTime',
  plannedStartDate: 'plannedStartDateTime',
  plannedFinishDate: 'plannedFinishDateTime',
  expectedStartDate: 'forecastedStartDateTime',
  expectedFinishDate: 'forecastedFinishDateTime',
  latestFinishDate: 'latestFinishDateTime',
  dependencies: 'dependencies',
};

const PLANNER_MODULE_ORDER_KEYS: ReadonlySet<string> = new Set(
  TABLE_COLUMN_SCHEDULE_FIELDS.flatMap((field) => COLUMN_TYPE_TO_ORDER_KEY[field] ?? []),
);

// Returned by reference when enabled: callers memoise on this set's identity.
export const withoutPlannerModuleOrderKeys = (
  keys: ReadonlySet<string>,
  plannerModuleEnabled: boolean,
): ReadonlySet<string> =>
  plannerModuleEnabled
    ? keys
    : new Set([...keys].filter((key) => !PLANNER_MODULE_ORDER_KEYS.has(key)));

export const isItemFieldSortable = (
  type: TableColumnSystemField,
  sortableKeys: ReadonlySet<string>,
): boolean => {
  const key = COLUMN_TYPE_TO_ORDER_KEY[type];
  return key != null && sortableKeys.has(key);
};

export const buildOrderString = (
  sort: SortDto | undefined,
  sortableKeys: ReadonlySet<string>,
  customFieldTypeById?: ReadonlyMap<number, CustomFieldTypes>,
): string | undefined => {
  if (!sort?.type) return undefined;
  const direction = sort.order === 'desc' ? 'DESC' : 'ASC';

  if (sort.type === 'customField') {
    if (!sort.customFieldId) return undefined;
    const fieldType = customFieldTypeById?.get(sort.customFieldId);
    if (fieldType !== undefined && !isCustomFieldTypeSortable(fieldType)) return undefined;
    return `customField(${sort.customFieldId}) ${direction}`;
  }

  const key = COLUMN_TYPE_TO_ORDER_KEY[sort.type as TableColumnSystemField];
  if (!key || !sortableKeys.has(key)) return undefined;

  if (key === 'itemType' && sortableKeys.has('itemLevel')) {
    return `itemLevel ${direction},itemType ${direction}`;
  }

  return `${key} ${direction}`;
};
